'use client';

import { Suspense } from 'react';

import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';

import { BookingServiceStart } from '@/components/booking/BookingServiceStart';
import { useCspNonce } from '@/components/security/CspNonceProvider';
import { RecaptchaEnterpriseScript } from '@/components/security/RecaptchaEnterpriseScript';
import { Skeleton } from '@/components/ui/skeleton';
import { useRouter } from '@/i18n/routing';
import {
  resolveBookingBusinessTypeAlias,
  resolveBookingServiceType,
} from '@/lib/booking/service-param';
import { resolveRecaptchaSiteKey } from '@/lib/security/recaptcha-site-key';
import { ServiceType } from '@/types/bookingFlow';

// Lazy load the heavy booking flow component (client-only to avoid SSR/hydration issues)
const MainBookingFlow = dynamic(
  () => import('@/MainBookingFlow').then(mod => ({ default: mod.MainBookingFlow })),
  {
    loading: () => <Skeleton className='h-96 w-full' />,
    ssr: false,
  },
);

// Inner component that uses useSearchParams - must be wrapped in Suspense
function BookingContentInner() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const serviceParam = searchParams.get('service');
  const preselectedService = resolveBookingServiceType(serviceParam);
  const businessTypeFromAlias = resolveBookingBusinessTypeAlias(serviceParam);

  // Extract preset parameters from URL (supports both tier preset and calculator preset)
  const tierPreset = (() => {
    if (preselectedService !== ServiceType.HOME_CLEANING) {
      return null;
    }

    // Check for tier preset (from HeroBookingSection)
    const tierParam = searchParams.get('tier');
    if (tierParam) {
      const sqmParam = searchParams.get('sqm');
      const roomsParam = searchParams.get('rooms');
      const bathsParam = searchParams.get('baths');

      return {
        tier: tierParam,
        squareFootage: sqmParam ? parseInt(sqmParam, 10) : undefined,
        rooms: roomsParam ? parseInt(roomsParam, 10) : undefined,
        bathrooms: bathsParam ? parseInt(bathsParam, 10) : undefined,
      };
    }

    // Check for calculator preset (from InstantPricingCalculator)
    const sqmParam = searchParams.get('sqm');
    const roomsParam = searchParams.get('rooms');
    const bathsParam = searchParams.get('baths');
    const frequencyParam = searchParams.get('frequency');
    const effortParam = searchParams.get('effort');

    if (sqmParam || roomsParam || bathsParam) {
      return {
        tier: null, // No tier number from calculator
        squareFootage: sqmParam ? parseInt(sqmParam, 10) : undefined,
        rooms: roomsParam ? parseInt(roomsParam, 10) : undefined,
        bathrooms: bathsParam ? parseInt(bathsParam, 10) : undefined,
        frequency: frequencyParam || undefined,
        effort: effortParam || undefined,
      };
    }

    return null;
  })();

  // Extract other service-specific presets
  const servicePreset = (() => {
    if (!preselectedService) {
      return null;
    }

    const preset: Record<string, unknown> = {};

    // Office cleaning presets
    if (preselectedService === ServiceType.OFFICE_CLEANING) {
      const sqm = searchParams.get('sqm');
      const baths = searchParams.get('baths');
      const frequency = searchParams.get('frequency');
      const cleaningCount = searchParams.get('cleaningCount');
      const effort = searchParams.get('effort');

      if (sqm) {
        preset.squareFootage = parseInt(sqm, 10);
      }
      if (baths) {
        preset.bathrooms = parseInt(baths, 10);
      }
      if (frequency) {
        preset.frequency = frequency;
      }
      if (cleaningCount) {
        preset.cleaningCount = parseInt(cleaningCount, 10);
      }
      if (effort) {
        preset.effort = effort;
      }
    }

    // Business cleaning presets
    if (preselectedService === ServiceType.BUSINESS_CLEANING) {
      const businessTypeParam = searchParams.get('businessType');
      if (businessTypeParam) {
        preset.businessType = businessTypeParam;
      } else if (businessTypeFromAlias) {
        preset.businessType = businessTypeFromAlias;
      }
    }

    // Move In/Out presets
    if (preselectedService === ServiceType.MOVE_IN_OUT) {
      const moveType = searchParams.get('moveType');
      const sqm = searchParams.get('sqm');
      const fromSqm = searchParams.get('fromSqm');
      const toSqm = searchParams.get('toSqm');

      if (moveType) {
        preset.moveType = moveType;
      }
      if (sqm) {
        preset.squareFootage = parseInt(sqm, 10);
      }
      if (fromSqm) {
        preset.fromSqm = parseInt(fromSqm, 10);
      }
      if (toSqm) {
        preset.toSqm = parseInt(toSqm, 10);
      }
    }

    // Post-Construction presets
    if (preselectedService === ServiceType.POST_CONSTRUCTION) {
      const sqm = searchParams.get('sqm');
      const level = searchParams.get('level');
      const windows = searchParams.get('windows');

      if (sqm) {
        preset.squareFootage = parseInt(sqm, 10);
      }
      if (level) {
        preset.level = level;
      }
      if (windows) {
        preset.windows = parseInt(windows, 10);
      }
    }

    // Window cleaning presets
    if (preselectedService === ServiceType.WINDOW_CLEANING) {
      const windows = searchParams.get('windows');
      if (windows) {
        preset.windows = parseInt(windows, 10);
      }
    }

    return Object.keys(preset).length > 0 ? preset : null;
  })();

  const handleExit = () => {
    router.push('/book');
  };

  const handleComplete = (bookingId: string) => {
    router.push(`/booking/confirmation?bookingId=${bookingId}`);
  };

  if (!preselectedService) {
    return (
      <BookingServiceStart
        onContinue={serviceType => {
          const params = new URLSearchParams(searchParams.toString());
          params.set('service', serviceType);
          router.push(`/book?${params.toString()}`);
        }}
      />
    );
  }

  return (
    <div className='min-h-screen bg-white'>
      <div className='mx-auto max-w-7xl px-0 sm:px-3 md:px-4'>
        <MainBookingFlow
          key={preselectedService}
          preselectedService={preselectedService}
          tierPreset={tierPreset}
          servicePreset={servicePreset}
          onExit={handleExit}
          onComplete={handleComplete}
        />
      </div>
    </div>
  );
}

// Export component wrapped in Suspense
export function BookingContent() {
  const nonce = useCspNonce();
  const recaptchaSiteKey = resolveRecaptchaSiteKey();

  return (
    <>
      {recaptchaSiteKey ? (
        <RecaptchaEnterpriseScript siteKey={recaptchaSiteKey} nonce={nonce} />
      ) : null}
      <Suspense
        fallback={
          <div className='min-h-screen bg-white flex items-center justify-center'>
            <Skeleton className='h-96 w-full' />
          </div>
        }
      >
        <BookingContentInner />
      </Suspense>
    </>
  );
}
